import { useMemo, useState } from "react";

import { useGetAdminV2FeatureAccessOrganisationsQuery } from "@/store/api/baseApi";
import type { AdminV2FeatureAccessEnvironment } from "@/types/adminV2";
import { VNextButton, VNextInput } from "@/vnext/primitives";

interface FeatureAccessOrganisationPickerProps {
  /** The site whose organisations to search (staging orgs differ from production). */
  environment: AdminV2FeatureAccessEnvironment;
  /** Label of `environment`, used to keep control names unique across panels. */
  environmentLabel: string;
  /** Org IDs already listed for the feature; hidden from results to avoid dupes. */
  knownOrganisationIds: Set<string>;
  /** Verb for the action button (e.g. "Add", "Exclude"). */
  actionLabel: string;
  /** Optional helper text shown beside the "Add an organisation" label. */
  hint?: string;
  disabled?: boolean;
  /**
   * Called once with every ticked organisation when the action button is
   * pressed, so the owner can batch the whole selection (one write pass, one
   * feedback message) instead of reacting per organisation.
   */
  onSelect: (organisations: Array<{ id: string; name: string }>) => void;
}

const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 10;

/**
 * Type-ahead organisation search for the Feature flags console.
 *
 * @remarks
 * Loads the target environment's lightweight organisation list once (cached)
 * and filters by name in the browser — the same pattern the Spoof customer
 * search uses — so it stays instant per keystroke. The list comes from the
 * environment-scoped feature-access endpoint because the sites' organisations
 * drift: a staging-only test organisation never exists in production, and
 * organisations created after the last staging refresh only exist in
 * production.
 *
 * Results render as a compact table with checkboxes so several organisations can
 * be ticked and added at once. Organisations already listed for the feature are
 * hidden so the same one cannot be added twice.
 */
export const FeatureAccessOrganisationPicker = ({
  environment,
  environmentLabel,
  knownOrganisationIds,
  actionLabel,
  hint,
  disabled = false,
  onSelect,
}: FeatureAccessOrganisationPickerProps) => {
  const [search, setSearch] = useState("");
  // Ticked results, kept as id -> name so a selection survives narrowing the
  // search and we can add them all without re-looking up the org.
  const [selectedResults, setSelectedResults] = useState<Map<string, string>>(
    () => new Map(),
  );
  const trimmed = search.trim();
  // One cached fetch of the full list; typing filters it locally (no per-key
  // server round-trip).
  const query = useGetAdminV2FeatureAccessOrganisationsQuery({ environment });
  const matches = useMemo(() => {
    if (trimmed.length < MIN_QUERY_LENGTH) {
      return [];
    }
    const needle = trimmed.toLowerCase();
    return (query.data?.organisations ?? [])
      .map((organisation) => ({
        id: organisation.id,
        name: organisation.name ?? organisation.id,
      }))
      .filter(
        (organisation) =>
          !knownOrganisationIds.has(organisation.id) &&
          organisation.name.toLowerCase().includes(needle),
      )
      .slice(0, MAX_RESULTS);
  }, [query.data, knownOrganisationIds, trimmed]);

  const selectedCount = selectedResults.size;
  const allResultsSelected =
    matches.length > 0 &&
    matches.every((organisation) => selectedResults.has(organisation.id));

  const toggleResult = (organisation: { id: string; name: string }) => {
    setSelectedResults((current) => {
      const next = new Map(current);
      if (next.has(organisation.id)) {
        next.delete(organisation.id);
      } else {
        next.set(organisation.id, organisation.name);
      }
      return next;
    });
  };

  const toggleSelectAllResults = () => {
    setSelectedResults((current) => {
      const next = new Map(current);
      for (const organisation of matches) {
        if (allResultsSelected) {
          next.delete(organisation.id);
        } else {
          next.set(organisation.id, organisation.name);
        }
      }
      return next;
    });
  };

  const addSelected = () => {
    onSelect([...selectedResults].map(([id, name]) => ({ id, name })));
    setSelectedResults(new Map());
  };

  return (
    <div className="vnext-stack">
      <label className="vnext-field">
        <span className="vnext-field__label">
          Add an organisation
          {hint ? (
            <span className="vnext-feature-search__label-hint">{hint}</span>
          ) : null}
        </span>
        <VNextInput
          aria-label={`Search ${environmentLabel} organisations by name`}
          className={
            query.isLoading
              ? "vnext-feature-search__input--searching"
              : undefined
          }
          disabled={disabled}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search organisations by name"
          value={search}
        />
      </label>
      {query.isLoading ? (
        <div className="vnext-feature-search__searching" role="status">
          <span aria-hidden="true" className="vnext-feature-search__pulse" />
          Loading organisations…
        </div>
      ) : query.isError ? (
        <div className="vnext-inline-warning">
          Organisations could not be loaded. Try again.
        </div>
      ) : trimmed.length < MIN_QUERY_LENGTH ? null : matches.length === 0 ? (
        <div className="vnext-empty">No matching organisations to add.</div>
      ) : (
        <>
          <div className="vnext-scroll-panel">
            <table className="vnext-table">
              <thead>
                <tr>
                  <th className="vnext-feature-row__select-cell">
                    <input
                      aria-label={`Select all ${environmentLabel} results`}
                      checked={allResultsSelected}
                      className="vnext-feature-row__checkbox"
                      disabled={disabled}
                      onChange={toggleSelectAllResults}
                      type="checkbox"
                    />
                  </th>
                  <th>Organisation</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((organisation) => (
                  <tr key={organisation.id}>
                    <td className="vnext-feature-row__select-cell">
                      <input
                        aria-label={`Select ${organisation.name}`}
                        checked={selectedResults.has(organisation.id)}
                        className="vnext-feature-row__checkbox"
                        disabled={disabled}
                        onChange={() => toggleResult(organisation)}
                        type="checkbox"
                      />
                    </td>
                    <td>{organisation.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selectedCount > 0 ? (
            <div className="vnext-inline-actions">
              <VNextButton
                disabled={disabled}
                onClick={addSelected}
                tone="primary"
              >
                {`${actionLabel} ${selectedCount} selected`}
              </VNextButton>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default FeatureAccessOrganisationPicker;
